import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import type Store from 'modules/attendance/components/ClockIn/Store'
import InconsistenciesAlertCountsTag from 'modules/attendance/components/InconsistenciesAlertCountsTag'
import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { Inconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useAttendancePolicies } from 'modules/attendance/lib/attendancePolicies'
import { useContractsVersion } from 'modules/attendance/lib/contractsVersionContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { PopoverTypeToShow } from 'modules/attendance/lib/types'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import { ErrorTypes, ValidationError } from 'modules/attendance/lib/validateShiftBreakDurationRules'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { useEmployeeProjectsTracking } from 'modules/project_management/contexts/EmployeeProjectsTrackingContext'

import OptionsButton from './OptionsButton'
import ShiftsInconsistenciesAlert from './ShiftsInconsistenciesAlert'

interface Props {
  date: moment.Moment
  store: Store
  shiftLocations?: AttendanceShiftLocationNodes
  open?: boolean
  toggle?: (value: boolean) => () => void
  restTimeInconsistencies?: ValidationError[]
  hasFutureDate?: boolean
  inconsistencies?: Inconsistencies
  attendanceSetting: TimesheetPageEmployeesSettingFragment
  shifts: TimesheetPageShiftFragment[]
}

const OptionsColumn = ({
  date,
  store,
  open,
  toggle,
  restTimeInconsistencies = [],
  hasFutureDate = false,
  inconsistencies,
  attendanceSetting,
  shifts,
}: Props) => {
  const { featureAvailability: projectsFeature } = useAttendanceFeatures(
    Features.projects_management
  )
  const { data } = useAttendancePolicies()
  const canEditAttendance = !!data?.employee?._policies?.editAttendance?.can
  const { startsOn } = useContractsVersion()
  const firstShift = shifts[0]
  const started = !!firstShift && !!firstShift.clockIn
  const { openShift, reviewsStats, attendancePeriods } = useTimesheetData()
  const year = date.year()
  const month = date.month() + 1
  const currentPeriod = attendancePeriods?.filter((p) => p.month === month && p.year === year)
  const hasEditTimesheetAuthorization = EmployeesSettingsUtils.hasEditTimesheetAuthorization([
    attendanceSetting,
  ])

  const readonly = AttendanceShiftUtils.isReadOnly({
    openShift,
    date,
    approvedDates: reviewsStats?.approvedDates ?? [],
    startsOn,
    canEditAttendance,
    currentPeriod,
  })

  const projectsContext = useEmployeeProjectsTracking()

  const numberOfBreakMinimumErrors =
    restTimeInconsistencies.filter((error) => error.error === ErrorTypes.BREAK_MINIMUM).length ?? 0

  const numberOfRestTimeErrors = Math.min(
    restTimeInconsistencies.filter((error) => error.error === ErrorTypes.REST_TIME).length ?? 0,
    1
  )

  const uniqueShiftInconsistencies =
    restTimeInconsistencies
      .filter((item) => item.error === ErrorTypes.BREAK_MINIMUM)
      .slice(0, 1)
      .concat(
        restTimeInconsistencies
          .filter((item) => item.error === ErrorTypes.REST_TIME)
          .sort((a, b) => (b.periodLength ?? 0) - (a.periodLength ?? 0))
          .slice(0, 1)
      ) ?? []

  const showShiftInconsistenciesAlert = !!uniqueShiftInconsistencies?.length

  const hasProjectsToTrack = projectsFeature && projectsContext?.queryData.hasProjectsToTrack
  const canManageProjects = policyAuthorizations.for(
    'projectmanagement.timerecord.create_records',
    {
      id: store.employeeId,
      policyTargetClass: '::Employee',
    }
  )
  const noOptions = !started || (readonly && !(hasProjectsToTrack && canManageProjects))

  const { featureAvailability: hasTimeTrackingInconsistencies } = useAttendanceFeatures(
    Features.time_tracking_time_alerts
  )

  const onClick = (e: React.BaseSyntheticEvent) => {
    e.stopPropagation()
    !!toggle && toggle(!open)()
    track(trackingEvents.timeTracking.expandOrCollapseDayRow, {
      action: !open ? 'expand' : 'collapse',
    })
  }

  return (
    <Box flexDirection="row" justifyContent="flexEnd" paddingX="s24" gap="s8" alignItems="center">
      <Box paddingRight="s16" maxWidth="fit-content">
        {showShiftInconsistenciesAlert && !hasTimeTrackingInconsistencies && (
          <ShiftsInconsistenciesAlert
            restTimeInconsistencies={uniqueShiftInconsistencies}
            numberOfBreakMinimumErrors={numberOfBreakMinimumErrors}
            numberOfRestTimeErrors={numberOfRestTimeErrors}
          />
        )}
        {hasTimeTrackingInconsistencies && (
          <InconsistenciesAlertCountsTag
            popoverTypeToShow={PopoverTypeToShow.Info}
            inconsistencies={inconsistencies}
            page="timesheet"
            timeRange="monthly"
          />
        )}
      </Box>
      <OptionsButton
        store={store}
        shifts={shifts}
        readonly={readonly}
        projectsContext={projectsContext}
        hasProjectsToTrackAndEnabled={hasProjectsToTrack}
        hasEditTimesheetAuthorization={hasEditTimesheetAuthorization}
        started={started}
        canManageProjects={canManageProjects}
        noOptions={noOptions}
      />
      <Button
        type="secondary"
        onClick={onClick}
        iconName={open ? 'chevron-up' : 'chevron-down'}
        disabled={hasFutureDate}
        extra={{ 'data-intercom-target': 'attendance-row-toggle' }}
      />
    </Box>
  )
}

export default observer(OptionsColumn)
